// Mirrors FleetHd.Issue ContributionDto (camelCase JSON). One piece of evidence
// (an Alert firing or a DVIR defect) asserting a problem is present.

import { EvidenceDto } from './evidence';

export const ContributionSourceTypes = {
  Alert: 'Alert', DVIRDefect: 'DVIRDefect', Manual: 'Manual',
} as const;
export type ContributionSourceType = typeof ContributionSourceTypes[keyof typeof ContributionSourceTypes];

// 'None' is the operator-report mode: human-asserted evidence never fades and has
// no climb ceiling, so the Issue closes only by disposition.
export type DecayMode = 'Persistence' | 'Contradiction' | 'None' | 'Unspecified';

export type ClosureReason = 'Decayed' | 'DvirRepaired' | 'Cleared' | 'Expired';

export interface MetricsDto {
  baseWeight: number;            // 0–1, asserted confidence at fire-time (frozen)
  currentDecayedWeight: number;  // 0–1, after decay; resets to base on re-fire
  ceiling: number;               // 0–1, max weight this contribution can ever climb to
  severityAsserted: number;      // 1–100, max asserted severity
}

// Per-contribution closure. Empty (no reason) means the contribution is still live.
export interface ClosureDto {
  reason: ClosureReason | '';
  closedAt: string;
  closedBySourceRefType: string;
  closedBySourceRefId: string;
  closedBySourceRefDefectId: string;
  closedByUserId: string;
}

export interface ContributionDto {
  contributionId: string;
  sourceType: ContributionSourceType;
  sourceRefAlertId: string;
  sourceRefDvirId: string;
  ruleId: string;
  slug: string;
  firstFiredAt: string;
  lastFiredAt: string;
  lastCorroboratedAt: string;
  weightAsOf: string;
  // DateTime.MaxValue when the evidence never expires, which is every Alert.
  expiresAt: string;
  fireCount: number;
  corroborationCount: number;       // debounce-gated re-fires driving the density climb
  corroborationCountAsOf: number;   // taps already consumed into the stored weight
  metrics: MetricsDto;
  decayMode: DecayMode;
  decayDeclarationJson: string;
  contradictionTriggeredAt: string;
  clearFadeHalfLifeHours: number;   // wall-clock fade half-life when the rule's ClearBehavior is Fade
  consecutiveOmissions: number;
  closure: ClosureDto;
  displayLabel: string;
  evidenceText: string;             // frozen-at-mint sentence, e.g. "SPN 111 FMI 3 (Coolant Level) — 3 occurrences"
  evidence: EvidenceDto;
}
